import {
	BrowserClient,
	defaultStackParser,
	getDefaultIntegrations,
	makeFetchTransport,
	Scope,
	type Event as SentryEvent,
} from '@sentry/browser';

import { fg } from '@atlaskit/platform-feature-flags';

import { BFFError, BFFNetworkError } from '../../common/errors';
import { getEnvironment } from '../environment';

import {
	DEFAULT_SENTRY_CONFIG,
	SENTRY_DSN,
	SUPPRESSED_BACKEND_ERROR_PATTERNS,
} from './constants';
import type { SentryClientConfig, SentryEventTags } from './types';

let sentryClient: BrowserClient | undefined;
let scope: Scope = new Scope();

/**
 * Returns true if the Sentry event represents a known backend/network failure
 * that the navigation FE cannot remediate. These are still tracked via
 * Splunk operational analytics (`errorBoundary.unhandledErrorCaught`).
 *
 * Uses the shared `SUPPRESSED_BACKEND_ERROR_PATTERNS` from `./constants` so that
 * the in-`beforeSend` filter and the runtime Sentry `ignoreErrors` filter (also
 * applied conditionally in `setupSentryClient` below) never drift apart.
 *
 * @internal Exported only so it can be unit-tested as a pure function.
 *           Not part of the public API of this package — do not import outside
 *           the `services/sentry` directory or its tests.
 */
export function isSuppressedBackendError(event: SentryEvent): boolean {
	// Match against the exception's raw `value` (not an assembled `"${type}: ${value}"`
	// string), and fall back to `event.message` when no exception is present. This
	// keeps the patterns in `SUPPRESSED_BACKEND_ERROR_PATTERNS` simple and avoids
	// false negatives caused by Sentry prepending the exception type to the message
	// (e.g. `"Error: Failed to fetch data: 500"` vs `"Failed to fetch data: 500"`).
	const exceptionValue = event.exception?.values?.[0]?.value ?? '';
	const candidates = [exceptionValue, event.message ?? ''].filter(Boolean);

	if (
		candidates.length > 0 &&
		SUPPRESSED_BACKEND_ERROR_PATTERNS.some((re) => candidates.some((c) => re.test(c)))
	) {
		return true;
	}

	// BFFNetworkError with 5xx is also BE-owned. We check `extra` because
	// `logException` flattens `BFFNetworkError` fields onto `scope.extras`.
	const extras = event.extra as { name?: string; status?: string | number } | undefined;
	if (extras?.name === 'BFF_NETWORK_ERROR') {
		const status = Number(extras.status);
		if (!Number.isNaN(status) && status >= 500) {
			return true;
		}
	}

	return false;
}

export function logException(exception: Error | unknown, tags: SentryEventTags): void {
	scope.setTags(tags);

	if (exception instanceof BFFError) {
		const error = exception as BFFError;

		scope.setExtras({
			name: error.name,
			context: error.context,
			status: error.status,
			timestamp: error.timestamp,
			originalError: error.originalError,
			...(error instanceof BFFNetworkError && { url: error.url }),
		});
	}

	sentryClient?.captureException(exception, undefined, scope);

	// Unset tags to prevent an instance of the error boundary from logging with a specific tag,
	// and then another instance from wrongly passing it despite not having the tag.
	for (const [key, _] of Object.entries(tags)) {
		scope.setTag(key, undefined);
	}

	// Unset any extras to prevent them from persisting
	scope.setExtras({});
}

export function setupSentryClient({
	sentryConfig,
}: {
	sentryConfig?: SentryClientConfig;
}): boolean {
	const { dsn = SENTRY_DSN, env } = sentryConfig || {};

	if (!sentryClient) {
		// SSR-safe check. If we are in SSR, we don't need a sentryClient
		if (typeof window === 'undefined') {
			return false;
		}

		const environment = getEnvironment(env);

		// filter integrations that use the global variable
		const integrations = getDefaultIntegrations({}).filter((defaultIntegration) => {
			return !['BrowserApiErrors', 'Breadcrumbs', 'GlobalHandlers'].includes(
				defaultIntegration.name,
			);
		});

		// Gate suppression of noisy backend / network errors behind a feature flag so
		// it can be ramped up safely (NAVX-4946). When the flag is off, behaviour is
		// identical to before this change (only `^SentryIgnore:` is ignored, and
		// `beforeSend` only drops events missing a `teamName` tag).
		const isSentryFilteringEnabled = fg('navx-4946-sentry-filtering');

		sentryClient = new BrowserClient({
			...DEFAULT_SENTRY_CONFIG,
			ignoreErrors: isSentryFilteringEnabled
				? [...DEFAULT_SENTRY_CONFIG.ignoreErrors, ...SUPPRESSED_BACKEND_ERROR_PATTERNS]
				: DEFAULT_SENTRY_CONFIG.ignoreErrors,
			dsn,
			environment,
			beforeSend: (event) => {
				// We only want errors captured explicitly via logException to avoid unwanted errors being sent
				if (event.tags?.teamName === undefined) {
					return null;
				}
				if (isSentryFilteringEnabled && isSuppressedBackendError(event)) {
					return null;
				}
				return event;
			},
			attachStacktrace: true,
			transport: makeFetchTransport,
			stackParser: defaultStackParser,
			integrations,
		});

		scope.setClient(sentryClient);
		sentryClient.init();
	}
	return true;
}

/**
 * Reset the Sentry client singleton. This is intended for testing purposes only.
 * @internal
 */
export function __resetSentryClient(): void {
	sentryClient = undefined;
	scope = new Scope();
}
